/**
 * A recurring expense as a value: the sheet that makes one, and the words a
 * schedule describes itself with.
 *
 * `src/screens/Recurring.tsx` is the same thing wearing React. Everything
 * that decides anything is here, as pure functions of a plain object, for the
 * reason `src/lib/split-draft.ts` gives about the expense sheet — and this
 * module is mostly that one. A rule *is* an expense template plus two dates,
 * so its money, its payers, its split, its preview and its "what is still
 * wrong" all come from `resolveSheet`, unchanged. What is added here is
 * only what a schedule has that an expense does not:
 *
 * **The 1st–28th, said before the request rather than after it.**
 * `server/recurring.ts` refuses a `startOn` past the 28th, because every
 * later date is exactly one calendar month on and a day in that range exists
 * in every month there is. That is the server's rule and nothing here can
 * talk it out of it; what this buys is somebody being told while the date
 * field is still in front of them, which is the same bargain
 * `src/lib/group-draft.ts` makes with the currency box.
 *
 * **A start date is not editable, so the sheet has two shapes.** `PATCH`
 * does not read `startOn`: changing which date a series falls on is changing
 * what the series is, and the honest way to do that is to cancel this one and
 * start another. `resolveRecurringSheet` still validates the field either
 * way — it is the same function — and the screen renders it as read-back
 * words rather than an input when it is correcting a rule.
 *
 * **Nothing in this app materialises an occurrence.** The scheduled Worker
 * does, nightly (`worker/wrangler.toml`), so a date that has come and gone
 * with no transaction against it is not a bug and the sentences below say so
 * rather than pretending the expense already exists.
 */

import type { Allocation, RecurringDraft, RecurringOccurrence, RecurringRule } from '../api'
import { formatDated } from './format'
import type { UserId } from './money'
import {
  newExpenseSheet,
  payerDraftFrom,
  resolveSheet,
  splitDraftFrom,
  type ExpenseSheet,
  type Problem,
  type SplitPreview,
} from './split-draft'

/** `MAX_START_DAY` in `server/recurring.ts`, restated so the field can say so. */
export const MAX_START_DAY = 28

/** `MAX_DESCRIPTION` and `MAX_NOTE` in `server/transactions.ts`, which reads both fields. */
export const MAX_DESCRIPTION = 200
export const MAX_NOTE = 2_000

/**
 * Everything the recurring sheet holds.
 *
 * `template` is the expense sheet itself, and its `occurredOn` is the *start*
 * date — the first occurrence, and the day of the month every later one falls
 * on. It is the same field because it is the same question ("which day?")
 * asked of the same form, and reusing it is what makes the amount pad, the
 * payer rules, the split editor and the preview identical to the sheet that
 * files an expense rather than a second implementation that can drift from it.
 */
export type RecurringSheet = {
  template: ExpenseSheet
  /** Never `repayment`: a settlement happens once, on the day it happens. */
  kind: 'expense' | 'loan'
  /** Blank is no note, not an empty one. */
  note: string
  /** Blank is a series with no end — it runs until somebody cancels it. */
  endOn: string
}

/** The start date, which the template holds as its `occurredOn`. */
export const startOnOf = (sheet: RecurringSheet): string => sheet.template.occurredOn

// ---------------------------------------------------------------------------
// Dates
// ---------------------------------------------------------------------------

const DATE_SHAPE = /^\d{4}-\d{2}-\d{2}$/

/** `YYYY-MM-DD`, and a date that exists — `2026-02-30` is not one. */
export function isRealDate(value: string): boolean {
  if (!DATE_SHAPE.test(value)) return false
  const parsed = new Date(`${value}T00:00:00Z`)
  return !Number.isNaN(parsed.getTime()) && parsed.toISOString().slice(0, 10) === value
}

const dayOf = (isoDate: string): number => Number(isoDate.slice(8, 10))

/**
 * Where the date field starts.
 *
 * Today, when today is a day every month has. On the 29th, 30th or 31st it is
 * the 1st of next month instead — because the alternative is a form that
 * opens on a date the server will refuse, and the fix a person would make to
 * it is the one this makes for them. Choosing the 28th instead would be a
 * quieter wrong answer: somebody setting up rent on the 31st means the end of
 * the month, not three days before it.
 */
export function suggestedStartOn(today: string): string {
  if (!isRealDate(today)) return today
  if (dayOf(today) <= MAX_START_DAY) return today
  const [year, month] = today.split('-').map(Number)
  const next = new Date(Date.UTC(year, month, 1))
  return next.toISOString().slice(0, 10)
}

/** `1` → `1st`, `22` → `22nd`. Only ever asked about 1–28, and general anyway. */
export function ordinalDay(day: number): string {
  const tens = day % 100
  if (tens >= 11 && tens <= 13) return `${day}th`
  switch (day % 10) {
    case 1:
      return `${day}st`
    case 2:
      return `${day}nd`
    case 3:
      return `${day}rd`
    default:
      return `${day}th`
  }
}

// ---------------------------------------------------------------------------
// Starting a sheet, and re-opening one
// ---------------------------------------------------------------------------

export function newRecurringSheet(options: {
  memberIds: readonly UserId[]
  myId: UserId
  today: string
}): RecurringSheet {
  return {
    template: newExpenseSheet({
      memberIds: options.memberIds,
      myId: options.myId,
      today: suggestedStartOn(options.today),
    }),
    kind: 'expense',
    note: '',
    endOn: '',
  }
}

/**
 * A saved rule, back in the sheet exactly as it was entered.
 *
 * The split and the payers come back through `src/lib/split-draft.ts`'s own
 * two functions — a rule stores the same inputs an expense does, in the same
 * shape, which is the whole point of `server/recurring.ts` storing inputs
 * rather than lines.
 */
export function recurringSheetFrom(rule: RecurringRule): RecurringSheet {
  return {
    template: {
      description: rule.description,
      category: rule.category,
      occurredOn: rule.startOn,
      amountCents: rule.amountCents,
      payers: payerDraftFrom(rule),
      split: splitDraftFrom(rule),
    },
    kind: rule.kind,
    note: rule.note ?? '',
    endOn: rule.endOn ?? '',
  }
}

// ---------------------------------------------------------------------------
// Resolving it
// ---------------------------------------------------------------------------

/** What is wrong with the start date, in words, or nothing. */
export function startOnProblem(startOn: string): Problem {
  if (!isRealDate(startOn)) return 'Pick the date it starts on.'
  if (dayOf(startOn) > MAX_START_DAY) {
    return `A repeating expense starts on the 1st–${MAX_START_DAY}th, so every month has that date to land on.`
  }
  return null
}

/** What is wrong with the end date, in words, or nothing. Blank is no end. */
export function endOnProblem(startOn: string, endOn: string): Problem {
  if (endOn.trim() === '') return null
  if (!isRealDate(endOn)) return 'That end date is not a date.'
  if (endOn < startOn) return 'The last one cannot be before the first one.'
  return null
}

export type ResolvedRecurringSheet = {
  /** Ready to send, or `null` while anything below is still a sentence. */
  draft: RecurringDraft | null
  preview: SplitPreview
  problems: {
    description: Problem
    amount: Problem
    payers: Problem
    split: Problem
    startOn: Problem
    endOn: Problem
  }
}

/**
 * The sheet, resolved: the preview to draw, what is still wrong, and — when
 * nothing is — the draft to send.
 *
 * The money half is `resolveSheet` and nothing else, so a recurring rule and
 * an expense are refused for the same reasons in the same words. What is
 * added is the two dates, and the note.
 */
export function resolveRecurringSheet(
  sheet: RecurringSheet,
  memberOrder: readonly UserId[],
): ResolvedRecurringSheet {
  const startOn = startOnOf(sheet)
  const resolved = resolveSheet(sheet.template, memberOrder)
  const problems = {
    ...resolved.problems,
    startOn: startOnProblem(startOn),
    endOn: endOnProblem(startOn, sheet.endOn),
  }

  if (resolved.draft === null || problems.startOn !== null || problems.endOn !== null) {
    return { draft: null, preview: resolved.preview, problems }
  }

  const note = sheet.note.trim()

  return {
    draft: {
      kind: sheet.kind,
      description: resolved.draft.description,
      category: resolved.draft.category,
      amountCents: resolved.draft.amountCents,
      payers: resolved.draft.payers,
      split: resolved.draft.split,
      note: note === '' ? null : note,
      startOn,
      endOn: sheet.endOn.trim() === '' ? null : sheet.endOn,
    },
    preview: resolved.preview,
    problems,
  }
}

/** The first problem worth putting under the button, in the order somebody meets them. */
export function firstProblem(problems: ResolvedRecurringSheet['problems']): Problem {
  return (
    problems.amount ??
    problems.description ??
    problems.payers ??
    problems.split ??
    problems.startOn ??
    problems.endOn
  )
}

// ---------------------------------------------------------------------------
// The words a schedule describes itself with
// ---------------------------------------------------------------------------

/**
 * Where a rule stands. `due` is a date that has come with nothing recorded
 * against it yet — normal, not broken: the Worker runs nightly, and until it
 * does the rule is a promise rather than an expense.
 */
export type RuleState = 'cancelled' | 'finished' | 'due' | 'scheduled'

/** Everything the sentences below read. A `RecurringRule` is one. */
export type Scheduled = {
  startOn: string
  nextOccursOn: string
  endOn: string | null
  deletedAt: string | null
}

export function ruleState(rule: Scheduled, today: string): RuleState {
  if (rule.deletedAt !== null) return 'cancelled'
  if (rule.endOn !== null && rule.nextOccursOn > rule.endOn) return 'finished'
  return rule.nextOccursOn <= today ? 'due' : 'scheduled'
}

/** "The 5th of every month" — the whole recurrence rule, which is all there is of one. */
export const describeSchedule = (rule: Scheduled): string =>
  `The ${ordinalDay(dayOf(rule.startOn))} of every month`

/**
 * What happens next, and when — or why nothing does.
 *
 * The nightly run is named in both of the states where it is the reason
 * somebody is looking at a date with nothing behind it. Saying "added" of an
 * expense that is not there yet would be the one lie this screen could tell:
 * a recurring rule is a promise until the Worker keeps it.
 */
export function describeNext(rule: Scheduled, today: string): string {
  switch (ruleState(rule, today)) {
    case 'cancelled':
      return 'Cancelled. Nothing more will be added, and what it already added stands.'
    case 'finished':
      return `Finished — it ran until ${formatDated(rule.endOn ?? rule.nextOccursOn)}.`
    case 'due':
      return rule.nextOccursOn === today
        ? 'Due today — tonight’s run will add it.'
        : `Due since ${formatDated(rule.nextOccursOn)} — the next nightly run will add it.`
    default:
      return `Next on ${formatDated(rule.nextOccursOn)}.`
  }
}

/**
 * What a rule says after somebody has skipped its upcoming occurrence.
 *
 * A skip does not move `nextOccursOn` — it writes a `recurring_occurrences`
 * row ahead of the date, and the Worker finds it there when the date comes
 * and advances the rule without making a transaction (`server/recurring.ts`).
 * So the rule still reads as due until tonight, and without this the screen
 * would look like the button had done nothing.
 *
 * It deliberately does not name the month it picks up again. That date is the
 * Worker's arithmetic, and restating it here would be a second implementation
 * of "one calendar month on" that could disagree with the one that matters.
 */
export const describeSkipped = (occursOn: string): string =>
  `Skipped — nothing will be added on ${formatDated(occursOn)}. It picks up again the month after.`

/** The end of a series that has one, said beside the schedule rather than instead of it. */
export function describeEnd(rule: Scheduled): string | null {
  if (rule.endOn === null || rule.deletedAt !== null) return null
  return `until ${formatDated(rule.endOn)}`
}

/** One line of history: a date, and which of the two things happened on it. */
export function describeOccurrence(occurrence: RecurringOccurrence): string {
  const when = formatDated(occurrence.occursOn)
  return occurrence.status === 'skipped'
    ? `${when} — skipped`
    : `${when} — added as an expense`
}

/** `['Ada', 'Ben', 'Cass']` → `Ada, Ben and Cass`. */
export function joinNames(names: readonly string[]): string {
  if (names.length === 0) return 'Nobody'
  if (names.length === 1) return names[0]
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`
}

/**
 * "Ada pays, split 3 ways" — who puts the money in, and between how many.
 *
 * The names arrive already resolved, with the reader's own replaced by
 * "You" — which is also why `plural` is a parameter rather than something
 * this works out from the list: one payer usually takes "pays", and "You
 * pays" is what guessing from the length alone would produce.
 *
 * There is deliberately no "and this is what each person owes" here. A rule
 * is inputs; what one occurrence costs whom is resolved on the day it fires,
 * and the expense it makes is where that number belongs.
 */
export function describeParties(
  payers: readonly string[],
  ways: number,
  plural: boolean,
): string {
  return `${joinNames(payers)} ${plural ? 'pay' : 'pays'}, split ${ways} way${
    ways === 1 ? '' : 's'
  }`
}

/**
 * Who the rule says pays, as ids in the order they were stored.
 */
export const payerIdsOf = (rule: { paid: readonly Allocation[] }): UserId[] =>
  rule.paid.map((allocation) => allocation.userId)
